import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RoleService } from "../../services/RoleService";
import PermissionModal from "../../components/admin/PermissionModal";

export default function RoleEdit() {
  const [role, setRole] = useState({
    name: "",
    description: "",
    permissionIds: [],
    active: true,
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const { roleId } = useParams();
  const navigate = useNavigate();
  const service = new RoleService();

  useEffect(() => {
    fetchPermissions();
    if (roleId) {
      setIsEditMode(true);
      fetchRole();
    } else {
      // No roleId means invalid access - redirect back
      navigate("/admin/roles");
    }
  }, [roleId]);

  const fetchPermissions = async () => {
    try {
      const response = await service.getPermissions();
      setPermissions(response.items || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      alert("Erro ao carregar permissões");
    }
  };

  const fetchRole = async () => {
    try {
      setLoading(true);
      const data = await service.findOne(roleId);

      // Extract permission IDs from populated permissions
      const permissionIds = data.permissions
        ? data.permissions.map(p => p._id || p)
        : data.permissionIds || [];

      setRole({
        ...data,
        permissionIds: permissionIds,
      });
    } catch (error) {
      console.error("Error fetching role:", error);
      alert("Erro ao carregar grupo");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setRole((prev) => ({ ...prev, [field]: value }));
  };

  const getTypeLabel = (type) => {
    const types = {
      ADMIN: "Administrador",
      AUTHENTICATED: "Autenticado",
      CUSTOM: "Personalizado",
    };
    return types[type] || type || "Personalizado";
  };

  const handlePermissionToggle = (permissionId) => {
    setRole((prev) => {
      const currentPermissions = prev.permissionIds || [];
      const isSelected = currentPermissions.includes(permissionId);

      if (isSelected) {
        return {
          ...prev,
          permissionIds: currentPermissions.filter((id) => id !== permissionId),
        };
      } else {
        return {
          ...prev,
          permissionIds: [...currentPermissions, permissionId],
        };
      }
    });
  };

  const handleSelectAll = () => {
    setRole((prev) => ({
      ...prev,
      permissionIds: permissions.map((p) => p._id),
    }));
  };

  const handleDeselectAll = () => {
    setRole((prev) => ({
      ...prev,
      permissionIds: [],
    }));
  };

  const handleCreatePermission = async (permissionData) => {
    try {
      const newPermission = await service.createPermission(permissionData);

      if (newPermission) {
        alert("Permissão criada com sucesso!");

        // Refresh permissions list
        await fetchPermissions();

        // Auto-select the new permission for current role
        setRole((prev) => ({
          ...prev,
          permissionIds: [...(prev.permissionIds || []), newPermission._id],
        }));

        setShowPermissionModal(false);
      }
    } catch (error) {
      console.error("Error creating permission:", error);
      alert("Erro ao criar permissão. Verifique se já não existe.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!role.name) {
      alert("Por favor, preencha o nome do grupo");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: role.name,
        description: role.description || "",
        permissionIds: role.permissionIds || [],
        active: role.active !== false,
      };

      await service.update(roleId, payload);
      alert("Grupo atualizado com sucesso!");

      navigate("/admin/roles");
    } catch (error) {
      console.error("Error saving role:", error);
      alert("Erro ao salvar grupo");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/roles");
  };

  if (loading) {
    return <div className="text-center mt-5">Carregando...</div>;
  }

  // Group permissions by category for better organization
  const groupedPermissions = permissions.reduce((acc, permission) => {
    const category = permission.label.split(".")[0] || "Geral";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(permission);
    return acc;
  }, {});

  return (
    <div className="container pt-5">
      <h2>Editar Grupo</h2>

      <form onSubmit={handleSubmit}>
        <div className="card mt-4">
          <div className="card-body">
            <h5 className="card-title mb-4">Informações do Grupo</h5>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Nome <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={role.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  disabled={role.type && role.type !== "CUSTOM"}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Tipo</label>
                <input
                  type="text"
                  className="form-control"
                  value={getTypeLabel(role.type)}
                  disabled
                  title="O tipo do grupo não pode ser alterado"
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={role.active ? "true" : "false"}
                  onChange={(e) => handleChange("active", e.target.value === "true")}
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Descrição</label>
              <textarea
                className="form-control"
                rows="3"
                value={role.descriptionPt || role.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Descrição opcional do grupo..."
              />
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="card-title mb-0">Permissões</h5>
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-success me-2"
                  onClick={() => setShowPermissionModal(true)}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Nova Permissão
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={handleSelectAll}
                >
                  Selecionar Todas
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleDeselectAll}
                >
                  Desmarcar Todas
                </button>
              </div>
            </div>

            <div className="alert alert-info mb-4">
              <i className="bi bi-info-circle me-2"></i>
              Selecione as permissões que este grupo terá no sistema.
              {role.permissionIds && role.permissionIds.length > 0 && (
                <span className="ms-2">
                  <strong>{role.permissionIds.length}</strong> permissão(ões) selecionada(s)
                </span>
              )}
            </div>

            {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
              <div key={category} className="mb-4">
                <h6 className="text-muted mb-3">
                  {category}
                  <span className="badge bg-secondary ms-2">
                    {categoryPermissions.filter(p => role.permissionIds?.includes(p._id)).length}/
                    {categoryPermissions.length}
                  </span>
                </h6>
                <div className="row">
                  {categoryPermissions.map((permission) => (
                    <div key={permission._id} className="col-md-6 col-lg-4 mb-2">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`perm-${permission._id}`}
                          checked={role.permissionIds?.includes(permission._id) || false}
                          onChange={() => handlePermissionToggle(permission._id)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`perm-${permission._id}`}
                        >
                          {permission.labelPt || permission.label}
                          {(permission.descriptionPt || permission.description) && (
                            <small className="text-muted d-block">
                              {permission.descriptionPt || permission.description}
                            </small>
                          )}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <hr className="my-3" />
              </div>
            ))}

            {permissions.length === 0 && (
              <div className="text-center text-muted py-4">
                Nenhuma permissão disponível
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 mb-5">
          <button
            type="submit"
            className="btn btn-primary me-2"
            disabled={saving}
          >
            {saving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Salvando...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Salvar Alterações
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            <i className="bi bi-x-lg me-2"></i>
            Cancelar
          </button>
        </div>
      </form>

      <PermissionModal
        show={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        onSave={handleCreatePermission}
      />
    </div>
  );
}